import { ChevronsUpDown, LogOutIcon, User2Icon } from 'lucide-react';

import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@data-delivery/d3s';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MinimalUser {
  given_name: string;
  family_name: string;
  name: string;
  email: string;
}

type UserProps = { user: MinimalUser; logOut: () => Promise<void> | void };

export function NavUser({ user, logOut }: UserProps) {
  const { isMobile } = useSidebar();
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  function navigateTo() {
    navigate('/user-info', {
      state: { from: location.pathname },
    });
  }

  async function handleLogout(e: React.MouseEvent) {
    e.preventDefault();
    setOpen(false);

    await logOut();
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                <AvatarFallback className='rounded-lg'>
                  {`${user.given_name.charAt(0)}${user.family_name.charAt(0)}`}
                </AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>{`${user.given_name} ${user.family_name}`}</span>
                <span className='truncate text-xs'>{user.email}</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarFallback className='rounded-lg'>
                    {`${user.given_name.charAt(0)}${user.family_name.charAt(0)}`}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {`${user.given_name} ${user.family_name}`}
                    {/* Roles removed */}
                  </span>
                  <span className='truncate text-xs'>{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={navigateTo}>
              <User2Icon className='mr-2' />
              User Info
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout}>
              <LogOutIcon className='mr-2' />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
